/**
 * Sidebar list row — single conversation entry.
 *
 * iter135.11 — extracted + memoised. Subscribes to ``useConv(id)``
 * so a typing tick or a message landing in OTHER conversations
 * doesn't re-render this row. Only the row whose conv changed
 * re-renders.
 */
import { memo } from "react";
import { Users } from "@phosphor-icons/react";

import { useConv } from "@/lib/conv/hooks";
import { parseServerTime } from "@/lib/conv/parseServerTime";

function SidebarRowImpl({ convId, selected, onSelect }) {
  const c = useConv(convId);
  if (!c) return null;
  const unread = (c.unread_count || 0) > 0;
  const dt = c.last_message_at ? parseServerTime(c.last_message_at) : null;
  const seedTitle = c.title || c.conversation_id || "?";
  const initial = (seedTitle.split("›").pop() || seedTitle).trim().charAt(0).toUpperCase() || "?";
  return (
    <button
      onClick={() => onSelect(convId)}
      data-testid={`conv-list-item-${convId}`}
      className={`w-full text-left flex items-start gap-3 px-3 py-3 border-b border-slate-100 transition-colors ${
        selected ? "bg-slate-100" : "hover:bg-slate-50"
      }`}
    >
      <div
        className="shrink-0 w-12 h-12 rounded-full flex items-center justify-center font-bold text-white text-base"
        style={{ background: avatarColor(seedTitle) }}
      >
        {initial}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={`text-[14px] truncate flex-1 ${unread ? "font-bold text-slate-900" : "font-medium text-slate-800"}`}>
            {c.title || "—"}
          </span>
          {c.archived && (
            <span
              className="text-[8px] font-bold px-1 py-0.5 rounded text-slate-500 border border-slate-300 uppercase tracking-wider shrink-0"
              data-testid={`conv-list-closed-${convId}`}
            >
              closed
            </span>
          )}
          {dt && (
            <span className={`text-[11px] shrink-0 ${unread ? "font-bold" : "text-slate-500"}`}
                  style={unread ? { color: "#00a884" } : undefined}>
              {formatListTime(dt)}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5 mt-0.5">
          <span className={`flex-1 text-[13px] truncate ${unread ? "text-slate-800" : "text-slate-500"}`}>
            {c.last_message_preview
              ? stripMentionMarkers(c.last_message_preview)
              : <span className="italic text-slate-400">no messages yet</span>}
          </span>
          {unread && (
            <span
              className="text-[10px] font-bold px-1.5 py-0.5 rounded-full text-white shrink-0"
              style={{ background: "#00a884" }}
            >
              {c.unread_count}
            </span>
          )}
        </div>
        <div className="text-[10px] text-slate-400 mt-0.5 flex items-center gap-1.5 truncate">
          <Users size={9} weight="bold" />
          <span className="truncate">
            {c.pcs_size ? `${c.pcs_size} ${c.pcs_size === 1 ? "company" : "companies"}` : ""}
          </span>
        </div>
      </div>
    </button>
  );
}

export const SidebarRow = memo(SidebarRowImpl);

// ── Helpers (kept here to avoid pulling them back from the page).

export function avatarColor(seed) {
  // Hash the seed string to pick a stable hue. WhatsApp-Web uses a
  // similar palette of muted greens/blues/oranges per chat.
  let h = 0;
  for (let i = 0; i < (seed || "").length; i++) {
    h = (h * 31 + seed.charCodeAt(i)) | 0;
  }
  const palette = ["#00a884", "#06875b", "#3a7ec0", "#9c5fbe", "#d97706", "#dc2626", "#0891b2"];
  return palette[Math.abs(h) % palette.length];
}

// iter136 — replace canonical mention markers with `@Name` so the
// sidebar preview line reads naturally instead of leaking the inline
// token (e.g. ``@[Gerry Gualtieri](person:11012)`` → ``@Gerry Gualtieri``).
const SIDEBAR_MENTION_RE =
  /@\[([^\]]{1,80})\]\((staff|person|everyone|here):(\*|\d{1,32})\)/g;
function stripMentionMarkers(text) {
  if (!text || typeof text !== "string") return text;
  return text.replace(SIDEBAR_MENTION_RE, (_full, name, kind) => {
    if (kind === "everyone" || kind === "here") return `@${kind}`;
    return `@${name}`;
  });
}

function formatListTime(d) {
  if (!d) return "";
  const today = new Date();
  const sameDay =
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate();
  if (sameDay) {
    return d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
  }
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday =
    d.getFullYear() === yesterday.getFullYear() &&
    d.getMonth() === yesterday.getMonth() &&
    d.getDate() === yesterday.getDate();
  if (isYesterday) return "Yesterday";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}
